import { useEffect, useMemo, useRef, useState } from "react";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { useDrag } from "@use-gesture/react";
import { useMixboard } from "@/state/useMixboard";

type GuideType = "verticalCenter" | "horizontalCenter";

interface DraggableLayerProps {
  layer: any;
  canvasCenterX: number;
  canvasCenterY: number;
  setGuideVisibility: (type: GuideType, visible: boolean) => void;
  setSelectedLayer: (id: string) => void;
  isSelected: boolean;
}

function DraggableLayer({
  layer,
  canvasCenterX,
  canvasCenterY,
  setGuideVisibility,
  setSelectedLayer,
  isSelected,
}: DraggableLayerProps) {
  const updateLayer = useMixboard((s) => s.updateLayer);
  const snap = (value: number, step = 10) => Math.round(value / step) * step;
  const dragOrigin = useRef<{ x: number; y: number }>({ x: layer.data?.x ?? 0, y: layer.data?.y ?? 0 });

  const bind = useDrag(({ movement: [mx, my], shiftKey, first, last }) => {
    if (first) {
      dragOrigin.current = {
        x: layer.data?.x ?? 0,
        y: layer.data?.y ?? 0,
      };
    }

    let dx = mx;
    let dy = my;
    if (shiftKey) {
      if (Math.abs(mx) >= Math.abs(my)) {
        dy = 0;
      } else {
        dx = 0;
      }
    }

    const snappedX = snap(dragOrigin.current.x + dx);
    const snappedY = snap(dragOrigin.current.y + dy);

    updateLayer(layer.id, {
      data: {
        ...layer.data,
        x: snappedX,
        y: snappedY,
      },
    });

    setGuideVisibility("verticalCenter", Math.abs(snappedX - canvasCenterX) < 10);
    setGuideVisibility("horizontalCenter", Math.abs(snappedY - canvasCenterY) < 10);

    if (last) {
      setGuideVisibility("verticalCenter", false);
      setGuideVisibility("horizontalCenter", false);
    }
  });

  return (
    <div
      {...bind()}
      onMouseDown={() => setSelectedLayer(layer.id)}
      style={{
        position: "absolute",
        left: layer.data?.x ?? 0,
        top: layer.data?.y ?? 0,
        outline: isSelected ? "2px solid #22d3ee" : "none",
        cursor: "move",
      }}
    >
      {layer.data?.url && <img src={layer.data.url} draggable={false} alt={layer.type} />}
    </div>
  );
}

export function CanvasArea() {
  const session = useMixboard((s) => s.session);
  const setSession = useMixboard((s) => s.setSession);
  const setLoading = useMixboard((s) => s.setLoading);
  const layers = useMixboard((s) => s.layers);
  const loading = useMixboard((s) => s.loading);
  const selectedLayerId = useMixboard((s) => s.selectedLayerId);
  const setSelectedLayerId = useMixboard((s) => s.setSelectedLayerId);
  const updateLayer = useMixboard((s) => s.updateLayer);
  const wsRef = useRef<WebSocket | null>(null);
  const [guides, setGuides] = useState<Record<GuideType, boolean>>({
    verticalCenter: false,
    horizontalCenter: false,
  });

  const canvasWidth = session?.canvas_width ?? 1024;
  const canvasHeight = session?.canvas_height ?? 1536;
  const canvasCenterX = canvasWidth / 2;
  const canvasCenterY = canvasHeight / 2;

  const setGuideVisibility = (type: GuideType, visible: boolean) => {
    setGuides((prev) => {
      if (prev[type] === visible) return prev;
      return { ...prev, [type]: visible };
    });
  };

  const resolveUserId = useMemo(() => {
    const win = typeof window === "undefined" ? undefined : (window as any);
    const globalUser = win?.currentUser || win?.user || win?.__USER__ || win?.__AGENT_USER__;
    return globalUser?.id;
  }, []);

  useEffect(() => {
    if (!session?.id) return;
    let ws: WebSocket | null = null;
    let reconnectTimeout: ReturnType<typeof setTimeout> | null = null;

    const cleanupTimers = () => {
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
        reconnectTimeout = null;
      }
    };

    const connectWS = () => {
      ws = new WebSocket(`${import.meta.env.VITE_WS_URL}/ws/mixboard/${session.id}`);
      wsRef.current = ws;

      ws.onopen = () => {
        const userId = resolveUserId || session?.user_id;
        if (userId) {
          ws?.send(JSON.stringify({ type: "presence", user: userId }));
        }
      };

      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        if (data?.type === "presence") {
          return;
        }
        const imageUrl = data?.image_url || data?.url;
        if (imageUrl) {
          setSession((prev: any) => ({ ...prev, last_render_url: imageUrl }));
          setLoading(false);
        }
      };

      ws.onclose = () => {
        cleanupTimers();
        reconnectTimeout = setTimeout(() => connectWS(), 1000);
      };
    };

    connectWS();

    return () => {
      cleanupTimers();
      wsRef.current = null;
      ws?.close();
    };
  }, [session?.id, session?.user_id, resolveUserId, setLoading, setSession]);

  useEffect(() => {
    const handleKeydown = (event: KeyboardEvent) => {
      if (!selectedLayerId) return;
      const layer = layers.find((l) => l.id === selectedLayerId);
      if (!layer) return;

      const step = event.shiftKey ? 10 : 1;
      let dx = 0;
      let dy = 0;

      switch (event.key) {
        case "ArrowLeft":
          dx = -step;
          break;
        case "ArrowRight":
          dx = step;
          break;
        case "ArrowUp":
          dy = -step;
          break;
        case "ArrowDown":
          dy = step;
          break;
        default:
          return;
      }

      event.preventDefault();
      const newData = {
        ...layer.data,
        x: (layer.data?.x ?? 0) + dx,
        y: (layer.data?.y ?? 0) + dy,
      };
      updateLayer(layer.id, { data: newData });
    };

    window.addEventListener("keydown", handleKeydown);
    return () => window.removeEventListener("keydown", handleKeydown);
  }, [layers, selectedLayerId, updateLayer]);

  useEffect(() => {
    const interval = setInterval(() => {
      wsRef.current?.send(JSON.stringify({ type: "ping" }));
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="flex-1 bg-background flex items-center justify-center relative">
      <TransformWrapper minScale={0.2} maxScale={4}>
        <TransformComponent>
          <div className="relative inline-block">
            {session?.last_render_url && (
              <img
                src={session.last_render_url}
                className="shadow-2xl rounded-lg"
                draggable={false}
                alt="Mixboard render"
                style={
                  session?.canvas_width && session?.canvas_height
                    ? { width: session.canvas_width, height: session.canvas_height }
                    : undefined
                }
              />
            )}
            {guides.verticalCenter && (
              <div className="pointer-events-none absolute top-0 bottom-0 left-1/2 w-px -translate-x-1/2 bg-pink-400" />
            )}
            {guides.horizontalCenter && (
              <div className="pointer-events-none absolute left-0 right-0 top-1/2 h-px -translate-y-1/2 bg-pink-400" />
            )}
            {layers.map((layer) => (
              <DraggableLayer
                key={layer.id}
                layer={layer}
                canvasCenterX={canvasCenterX}
                canvasCenterY={canvasCenterY}
                setGuideVisibility={setGuideVisibility}
                setSelectedLayer={setSelectedLayerId}
                isSelected={selectedLayerId === layer.id}
              />
            ))}
          </div>
        </TransformComponent>
      </TransformWrapper>
      {loading && (
        <div className="absolute inset-0 bg-black/40 flex items-center justify-center text-foreground text-xl font-semibold">
          Rendering…
        </div>
      )}
    </div>
  );
}
